"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SizeSelector from "@/components/size-selector";
import Container from "@/components/ui/container";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Award,
  TrendingUp,
  ShoppingBag,
  Star,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";

function RockBoardPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesPerView = 3;
  const totalSlides = Math.ceil(products.length / slidesPerView);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const visibleProducts = products.slice(
    currentSlide * slidesPerView,
    currentSlide * slidesPerView + slidesPerView
  );
  return (
    <div className='min-h-screen'>
      {/* Hero Section */}
      <section className='py-20'>
        <div className='container mx-auto px-4'>
          <div className='flex flex-col items-center text-center'>
            <Building2 className='h-16 w-16 text-primary mb-6' />
            <h1 className='text-4xl font-bold mb-4'>Rock Board®</h1>
            <p className='text-xl text-gray-600 max-w-2xl mb-8'>
              Crafting exceptional products since 1990. We combine innovation,
              quality, and sustainability to deliver premium solutions.
            </p>
            <Link href='/rock-board/products'>
              <Button className='bg-primary hover:bg-primary/80'>
                Explore Our Collection
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Top Products Section with Carousel */}
      <section className='py-16'>
        <div className='container mx-auto px-4'>
          <div className='flex justify-between items-center mb-12'>
            <h2 className='text-3xl font-bold'>Our Top-Selling Products</h2>
            <Link href='/rock-board/products'>
              <Button variant='outline'>
                Browse All Products
                <ArrowRight className='ml-2 h-4 w-4' />
              </Button>
            </Link>
          </div>
          <div className='relative'>
            <div className='grid grid-cols-3 gap-8'>
              {visibleProducts.map((product) => (
                <Card
                  key={product.id}
                  className='overflow-hidden flex flex-col'
                >
                  <div className='relative aspect-[4/3] w-full'>
                    <Image
                      src={product.image}
                      alt={product.name}
                      className='object-cover'
                      fill
                      sizes='(max-width: 768px) 100vw, 33vw'
                      priority
                    />
                  </div>
                  <CardHeader>
                    <div className='flex justify-between items-start'>
                      <div>
                        <CardTitle className='text-xl mb-2'>
                          {product.name}
                        </CardTitle>
                        <Badge variant='secondary' className='mb-2'>
                          {product.category}
                        </Badge>
                      </div>
                      <span className='text-lg font-bold text-primary'>
                        {product.price}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className='text-gray-600 mb-4'>{product.description}</p>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center'>
                        <Star className='h-5 w-5 text-yellow-400 mr-1 fill-current' />
                        <span className='text-sm text-gray-600'>
                          {product.rating} / 5.0
                        </span>
                      </div>
                      <Button variant='outline' size='sm'>
                        Learn More
                        <ArrowRight className='ml-2 h-4 w-4' />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Carousel Navigation */}
            <div className='flex justify-center mt-8 gap-4'>
              <Button
                variant='outline'
                size='icon'
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className='rounded-full'
              >
                <ChevronLeft className='h-4 w-4' />
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={nextSlide}
                disabled={currentSlide === totalSlides - 1}
                className='rounded-full'
              >
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Values */}
      <section className='py-16 bg-black'>
        <div className='container mx-auto px-4'>
          <h2 className='text-3xl font-bold text-center mb-12'>
            Why Choose Us
          </h2>
          <div className='space-y-24'>
            {/* Feature 1 */}
            <div className='flex flex-col md:flex-row items-center justify-between gap-12'>
              <Image
                src='/images/hero01.webp'
                alt='Premium Quality'
                className='rounded-lg w-full md:w-1/2 object-cover'
                width={500}
                height={300}
              />
              <div className='w-full md:w-1/2'>
                <div className='flex items-center gap-3 mb-4'>
                  <Award className='h-8 w-8 text-primary' />
                  <h3 className='text-2xl font-bold'>Premium Quality</h3>
                </div>
                <p className='text-gray-600 text-lg leading-relaxed'>
                  Our commitment to excellence ensures the highest quality in
                  every product. We source the finest materials and employ
                  rigorous quality control processes to deliver products that
                  exceed your expectations. Each item undergoes extensive
                  testing to ensure durability and performance.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className='flex flex-col md:flex-row-reverse items-center justify-between gap-12'>
              <Image
                src='/images/hero01.webp'
                alt='Premium Quality'
                className='rounded-lg w-full md:w-1/2 object-cover'
                width={500}
                height={300}
              />
              <div className='w-full md:w-1/2'>
                <div className='flex items-center gap-3 mb-4'>
                  <TrendingUp className='h-8 w-8 text-primary' />
                  <h3 className='text-2xl font-bold'>Innovation</h3>
                </div>
                <p className='text-gray-600 text-lg leading-relaxed'>
                  Pushing boundaries with cutting-edge technology and design.
                  Our R&D team constantly explores new possibilities to bring
                  you innovative solutions that make your life easier. We invest
                  heavily in research and development to stay ahead of industry
                  trends.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className='flex flex-col md:flex-row items-center justify-between gap-12'>
              <Image
                src='/images/hero01.webp'
                alt='Premium Quality'
                className='rounded-lg w-full md:w-1/2 object-cover'
                width={500}
                height={300}
              />
              <div className='w-full md:w-1/2'>
                <div className='flex items-center gap-3 mb-4'>
                  <ShoppingBag className='h-8 w-8 text-primary' />
                  <h3 className='text-2xl font-bold'>Customer First</h3>
                </div>
                <p className='text-gray-600 text-lg leading-relaxed'>
                  Dedicated to providing exceptional customer experience. Our
                  support team is available 24/7 to assist you with any
                  questions or concerns. We believe in building lasting
                  relationships with our customers through trust and excellent
                  service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Brand Section */}
      <section className='py-16'>
        <div className='container mx-auto px-4'>
          <div className='max-w-3xl mx-auto text-center'>
            <h2 className='text-3xl font-bold mb-6'>About Our Brand</h2>
            <p className='text-gray-600 mb-8'>
              With over three decades of experience, our brand has become
              synonymous with excellence and innovation. We take pride in our
              commitment to sustainability, quality craftsmanship, and customer
              satisfaction.
            </p>
            <div className='grid grid-cols-3 gap-8 mb-8'>
              <div>
                <h3 className='text-2xl font-bold text-primary'>30+</h3>
                <p className='text-gray-600'>Years of Excellence</p>
              </div>
              <div>
                <h3 className='text-2xl font-bold text-primary'>1M+</h3>
                <p className='text-gray-600'>Happy Customers</p>
              </div>
              <div>
                <h3 className='text-2xl font-bold text-primary'>50+</h3>
                <p className='text-gray-600'>Countries Served</p>
              </div>
            </div>
            <Button variant='outline'>Learn More About Our Story</Button>
          </div>
        </div>
      </section>

      <Container className='py-20 flex flex-col items-center'>
        <h2 className='text-3xl font-bold mb-6'>
          Need help on deciding the right size?
        </h2>
        <SizeSelector />
      </Container>
    </div>
  );
}
export default RockBoardPage;

// Sample product data - replace with your actual data
const products = [
  {
    id: 1,
    name: "Premium Product 1",
    description: "Our flagship product with exceptional quality",
    price: "$199.99",
    rating: 4.8,
    category: "Best Seller",
    image: "/images/hero01.webp",
  },
  {
    id: 2,
    name: "Premium Product 2",
    description: "Innovative design meets functionality",
    price: "$149.99",
    rating: 4.7,
    category: "New Arrival",
    image: "/images/hero01.webp",
  },
  {
    id: 3,
    name: "Premium Product 3",
    description: "Perfect blend of style and performance",
    price: "$179.99",
    rating: 4.9,
    category: "Featured",
    image: "/images/hero01.webp",
  },
  {
    id: 4,
    name: "Premium Product 4",
    description: "Advanced features for professionals",
    price: "$229.99",
    rating: 4.6,
    category: "Best Seller",
    image: "/images/hero01.webp",
  },
];
